/**
 * academyModel — 학생 v2 화면의 계산(상태 배지 · 반 정렬 · 기간 띠 · 강사 숫자 띠 · 이력 묶음).
 * 그리는 부품과 떼어 둔 이유: 숫자를 테스트로 못 박기 위해서다(설계 §11-2 — 화면 숫자가 요청서 계산과 같은가).
 * 입력은 academyApi 가 화면 모양으로 바꾼 값이다.
 */
import {
  addDays, daysBetween, enrollmentLastDay, elapsedDays, formatMonthDay, fromDayNumber, gradeLabel, isoWeekday, lastDayOf, tenureLastDay, toDayNumber, withdrawalDay,
} from './periodDates';

// ── 상태(설계 §2-1) ──

/** 왼쪽 표 「상태」 칸 고르기 — 기본은 「전체」. 「재원」은 수강 중 + 휴원(퇴원 예정 포함) */
export const STATE_FILTERS = [
  { key: 'active', label: '재원', states: ['enrolled', 'paused'] },
  { key: 'enrolled', label: '수강 중', states: ['enrolled'] },
  { key: 'paused', label: '휴원', states: ['paused'] },
  { key: 'withdraw_scheduled', label: '퇴원 예정', states: ['withdraw_scheduled'] },
  { key: 'register_scheduled', label: '등록 예정', states: ['register_scheduled'] },
  { key: 'withdrawn', label: '퇴원', states: ['withdrawn'] },
  // 재원 기록이 하나도 없는 학생 — 학생 정보만 만든 학생 · 학생 앱으로 가입했거나 v1 에만 있던 계정. 여기서 찾아 [등록]한다(백엔드 2단계 전달 §4-E).
  // 이름은 「등록 전」 — 「미등록」은 문의 설계의 다른 뜻이라 쓰지 않는다(백엔드 2b 전달 §3-C)
  { key: 'none', label: '등록 전', states: ['none'] },
  { key: 'all', label: '전체', states: ['all'] },
];
export const stateFilterOf = (key) => STATE_FILTERS.find((f) => f.key === key) || STATE_FILTERS[0];

/**
 * 상태 배지 — 퇴원 예정은 재원생(수강 중·휴원) 위에 얹히는 표시라 먼저 본다(요청서 S1 withdraw_scheduled_on).
 * tone: ok 초록 · warn 노랑 · danger 빨강 · info 파랑 · muted 회색
 */
export function stateBadge(student = {}) {
  const { state } = student;
  const wd = withdrawalDay(student);
  if (wd && (state === 'enrolled' || state === 'paused')) {
    return { key: 'withdraw_scheduled', label: '퇴원 예정', tone: 'danger', date: formatMonthDay(wd) };
  }
  switch (state) {
    case 'enrolled': return { key: 'enrolled', label: '수강 중', tone: 'ok', date: '' };
    case 'paused': return { key: 'paused', label: '휴원', tone: 'warn', date: '' };
    case 'register_scheduled': return { key: 'register_scheduled', label: '등록 예정', tone: 'info', date: '' };
    case 'withdrawn': return { key: 'withdrawn', label: '퇴원', tone: 'muted', date: '' };
    default: return { key: 'none', label: '등록 전', tone: 'muted', date: '' };
  }
}

// ── 반(설계 §2 셋째 칸) ──

/** 입반일 순. 강사 계정이면 본인이 담임인 반을 먼저 — 같은 무리 안에서는 입반일 순 */
export function orderClasses(classes = [], myStaffId = null) {
  const byStart = [...classes].sort((a, b) => String(a.start_on || '').localeCompare(String(b.start_on || '')));
  if (myStaffId == null) return byStart;
  const mine = (c) => c.teacher && c.teacher.staff_id === myStaffId;
  return [...byStart.filter(mine), ...byStart.filter((c) => !mine(c))];
}

/** 셋째 칸 두 줄 — 첫 반 + 「+N」 / 그 반 담임. 마우스를 올리면 모든 반과 담임 */
export function classCell(classes = [], myStaffId = null) {
  const ordered = orderClasses(classes, myStaffId);
  if (ordered.length === 0) return { first: null, more: 0, teacher: '-', title: '반 없음' };
  return {
    first: ordered[0].class_name,
    more: ordered.length - 1,
    teacher: ordered[0].teacher?.name || '담임 없음',
    title: ordered.map((c) => `${c.class_name}(${c.teacher?.name || '담임 없음'})`).join(' · '),
  };
}

// ── 학생 상세 ──

/** 재원 일수 — 재원 기간마다 기준일까지 센 날의 합(요청서 SC7: 74 + 39 = 113) */
export const totalTenureDays = (tenures = [], asOf) => tenures.reduce((sum, t) => sum + elapsedDays(t.start_on, t.end_on, asOf), 0);

const END_KIND = {
  TRANSFER: (e) => ({ text: `이동 → ${e.transfer_to_class_name || '다른 반'}`, tone: 'info' }),
  WITHDRAW: () => ({ text: '퇴원', tone: 'muted' }),
  // 「종강」은 없다 — 반은 비활성화만 하고, 학생이 남은 반은 비활성화할 수 없다(백엔드 4단계 전달 §4-D).
  // 「기타」는 사유 값의 이름이라 끝 글자는 「퇴반」(사유가 있으면 「퇴반 — 사유」)
  OTHER: (e) => ({ text: e.end_reason ? `퇴반 — ${e.end_reason}` : '퇴반', tone: 'muted' }),
};

/**
 * 반 편성 탭의 줄 — 최근 입반 먼저. 일수 = 저장 경계 end_on − 입반일(마지막 수강일 포함, 진행 중이면 기준일까지).
 * 마지막 수강일이 기준일 당일이거나 뒤면 「퇴반 예정」. 퇴원 예정으로 퇴원일에 저절로 닫힌 편성(auto_closed)은 「퇴원 예정」.
 * movable — [반 이동] · [퇴반]을 할 수 있는 줄: 진행 중이거나 퇴원 예정으로만 닫힌 줄. 앞으로 퇴반 · 이동이 잡힌 줄은
 * 서버가 409 invalid_state 로 거절한다(날짜는 정정으로, 백엔드 4단계 전달 §4-C).
 */
export function enrollmentRows(enrollments = [], asOf) {
  return [...enrollments]
    .sort((a, b) => String(b.start_on || '').localeCompare(String(a.start_on || '')))
    .map((e) => {
      const open = !e.end_on;
      const future = !open && e.end_on > asOf;
      let end;
      if (open) end = { text: '진행 중', tone: 'ok' };
      else if (future && e.auto_closed) end = { text: `퇴원 예정 ${formatMonthDay(lastDayOf(e.end_on))}`, tone: 'danger' };
      else if (future && e.end_kind === 'TRANSFER') end = { text: `반 이동 예정 ${formatMonthDay(e.end_on)}`, tone: 'info' };
      else if (future) end = { text: `퇴반 예정 ${formatMonthDay(enrollmentLastDay(e))}`, tone: 'danger' };
      else end = (END_KIND[e.end_kind] || END_KIND.OTHER)(e);
      return {
        ...e,
        last_day: enrollmentLastDay(e),
        current: open || future,
        // 퇴원 예정으로만 닫힌 줄만 옮기거나 퇴반할 수 있다 — 서버는 이미 지난 퇴원으로 닫힌 줄도 auto_closed 로 준다(백엔드 6단계 전달 §2-2)
        movable: open || (future && !!e.auto_closed),
        days: open ? elapsedDays(e.start_on, null, asOf) : daysBetween(e.start_on, e.end_on),
        teachersText: e.teachers.length ? e.teachers.map((t) => t.name).join(' → ') : '담임 없음',
        endLabel: end,
      };
    });
}

/** 기간 띠 한 막대의 모양 — 지난 것은 옅게, 휴원은 노랑 */
const toneOf = (p, asOf) => {
  if (p.end_on && p.end_on <= asOf) return 'past';
  return 'cur';
};
const visibleEnd = (p, asOf) => (p.end_on && p.end_on <= asOf ? p.end_on : null);
const spanText = (p, asOf, endWord) => {
  const end = visibleEnd(p, asOf);
  return `${formatMonthDay(p.start_on)} ~ ${end ? `${formatMonthDay(end)}${endWord ? `(${endWord})` : ''}` : '오늘'}`;
};

/**
 * 개요 탭 기간 띠(설계 §4-1) — 재원 · 휴원 · 반(반마다 한 줄) · 학교 · 부서.
 * 범위는 가장 이른 실제 기록일부터 기준일(오늘)까지. 아직 시작하지 않은 기간은 제외한다.
 */
export function buildLanes(detail, asOf) {
  const historical = Object.fromEntries(['tenures', 'pauses', 'enrollments', 'school_periods', 'department_periods']
    .map((key) => [key, detail[key].filter((p) => p?.start_on && p.start_on <= asOf)]));
  const all = Object.values(historical).flat();
  const starts = all.map((p) => p.start_on).sort();
  const rangeStart = starts[0] || asOf;
  const rangeEnd = asOf;

  const lanes = [];
  const multi = historical.tenures.length > 1;
  lanes.push({
    key: 'tenure',
    label: '재원',
    bars: historical.tenures.map((t, i) => ({
      start_on: t.start_on, end_on: visibleEnd(t, asOf), tone: toneOf(t, asOf),
      display_end_on: tenureLastDay(t) && tenureLastDay(t) <= asOf ? tenureLastDay(t) : null, end_inclusive: true,
      text: multi ? `${t.seq || i + 1}차 재원` : '재원',
      summary: `${elapsedDays(t.start_on, t.end_on, asOf)}일${visibleEnd(t, asOf) && t.withdraw_reason ? ` · ${t.withdraw_reason}` : ''}`,
      tip: `${formatMonthDay(t.start_on)} ~ ${tenureLastDay(t) && tenureLastDay(t) <= asOf ? `${formatMonthDay(tenureLastDay(t))}(마지막 수강일)` : '오늘'} · ${elapsedDays(t.start_on, t.end_on, asOf)}일${visibleEnd(t, asOf) && t.withdraw_reason ? ` · ${t.withdraw_reason}` : ''}`,
    })),
  });
  if (historical.pauses.length) {
    lanes.push({
      key: 'pause',
      label: '휴원',
      bars: historical.pauses.map((p) => ({
        start_on: p.start_on, end_on: visibleEnd(p, asOf), tone: 'pause', text: '',
        summary: `${elapsedDays(p.start_on, p.end_on, asOf)}일${p.reason ? ` · ${p.reason}` : ''}${visibleEnd(p, asOf) ? p.auto_closed ? ' · 퇴원' : ' · 복귀' : ''}`,
        // 퇴원일에 저절로 닫힌 휴원(auto_closed)의 끝은 복귀가 아니라 퇴원이다(백엔드 5단계 전달 §2)
        tip: `휴원 ${spanText(p, asOf, p.auto_closed ? '자동 종료일' : '복귀일')} · ${elapsedDays(p.start_on, p.end_on, asOf)}일${p.reason ? ` · ${p.reason}` : ''}`,
      })),
    });
  }
  const byClass = new Map();
  [...historical.enrollments].sort((a, b) => String(a.start_on).localeCompare(String(b.start_on))).forEach((e) => {
    const key = e.class_id ?? e.class_name;
    if (!byClass.has(key)) byClass.set(key, { key: `class-${key}`, label: e.class_name, bars: [] });
    const end = visibleEnd(e, asOf);
    const last = enrollmentLastDay(e);
    const displayEnd = last && last <= asOf ? last : null;
    const days = elapsedDays(e.start_on, e.end_on, asOf);
    const why = end && e.end_kind ? (END_KIND[e.end_kind] || END_KIND.OTHER)(e).text : '';
    byClass.get(key).bars.push({
      start_on: e.start_on, end_on: end, tone: toneOf(e, asOf),
      display_end_on: displayEnd, end_inclusive: true,
      text: `${formatMonthDay(e.start_on)}~${displayEnd ? formatMonthDay(displayEnd) : '오늘'}`,
      summary: `${days}일${why ? ` · ${why}` : ''}`,
      tip: `${e.class_name} · ${formatMonthDay(e.start_on)} ~ ${displayEnd ? `${formatMonthDay(displayEnd)}(마지막 수강일)` : '오늘'} · ${days}일${why ? ` · ${why}` : ''}`,
    });
  });
  if (byClass.size) lanes.push(...byClass.values());
  else lanes.push({ key: 'class-none', label: '반', bars: [] });
  lanes.push({
    key: 'school',
    label: '학교',
    bars: historical.school_periods.map((p) => ({
      start_on: p.start_on, end_on: visibleEnd(p, asOf), tone: toneOf(p, asOf),
      text: `${p.school?.name || '학교 미지정'} · ${p.grade_label || gradeLabel(p.grade_at_start)}`,
      summary: p.estimated ? '이관(추정)' : '',
      tip: `${spanText(p, asOf)} · 학년 ${p.grade_at_start ?? '-'}${p.estimated ? ' · 이관(추정)' : ''}`,
    })),
  });
  lanes.push({
    key: 'department',
    label: '부서',
    bars: historical.department_periods.map((p) => ({
      start_on: p.start_on, end_on: visibleEnd(p, asOf), tone: toneOf(p, asOf),
      text: p.department?.name || '부서 없음',
      tip: spanText(p, asOf),
    })),
  });

  // 눈금 — 달 수에 맞춰 1·2·3·6 달 간격(눈금이 6개 안팎이 되게)
  const months = (Number(rangeEnd.slice(0, 4)) - Number(rangeStart.slice(0, 4))) * 12 + Number(rangeEnd.slice(5, 7)) - Number(rangeStart.slice(5, 7)) + 1;
  const step = months <= 6 ? 1 : months <= 12 ? 2 : months <= 24 ? 3 : 6;
  const multiYear = rangeStart.slice(0, 4) !== rangeEnd.slice(0, 4);
  const ticks = [];
  for (let k = 0; k < months; k += step) {
    const d = new Date(Date.UTC(Number(rangeStart.slice(0, 4)), Number(rangeStart.slice(5, 7)) - 1 + k, 1)).toISOString().slice(0, 10);
    const mm = Number(d.slice(5, 7));
    // 좁은 칸(휴대폰 약 230px)에서 「26년 3월」이 옆 눈금과 겹쳐 짧게 쓴다 — 한 해 안이면 달만(연도는 rangeLabel),
    // 해를 넘기면 「24.4」(달만 쓰면 「4월 10월 4월 10월」이 되어 몇 년인지 모름)
    ticks.push({ date: d < rangeStart ? rangeStart : d, label: multiYear ? `${d.slice(2, 4)}.${mm}` : `${mm}월` });
  }
  const rangeLabel = `${rangeStart.replace(/-/g, '.')} ~ ${rangeEnd.replace(/-/g, '.')}`;
  return { rangeStart, rangeEnd, rangeLabel, lanes, ticks, today: asOf };
}

/** 날짜의 띠 위 위치(0~100%) — 범위 끝날의 다음 날을 100% 로 */
export function lanePercent(date, rangeStart, rangeEnd) {
  const a = toDayNumber(rangeStart);
  const span = toDayNumber(rangeEnd) + 1 - a;
  const d = date == null ? toDayNumber(rangeEnd) + 1 : toDayNumber(date);
  return Math.max(0, Math.min(100, ((d - a) / span) * 100));
}

// ── 강사 통계(설계 §3-5) — 통계 API(ST*) 없이 목록·반·일정으로 계산 ──

const overlaps = (s, day) => s.start_on <= day && day < s.end_on;

/** 반 하나의 다가오는 수업일 — today 부터 days 일, 그날 담임 일정이 겹치면 absent 에 일정 종류 */
export function upcomingClassDays(cls, schedules = [], today, days = 7) {
  const wd = new Set((cls.slots || []).map((s) => Number(s.weekday)));
  const t0 = toDayNumber(today);
  const out = [];
  for (let k = 0; k < days; k += 1) {
    const day = fromDayNumber(t0 + k);
    if (wd.has(isoWeekday(day))) {
      const hit = schedules.find((s) => overlaps(s, day));
      out.push({ date: day, weekday: isoWeekday(day), absent: hit ? (hit.kind || hit.title || '일정') : null });
    }
  }
  return out;
}

/**
 * 강사 숫자 띠 넷 — 내 반 재원(중복 없이 · 그중 휴원) · 30일 안 퇴원 예정 · 이번 주 수업 · 담임 부재 예정.
 * students 는 강사 범위의 재원생 목록(S1), classes 는 본인이 담임인 반(C1), schedules 는 본인 일정(E9).
 * 「내 반 재원」 · 「30일 안 퇴원 예정」은 오늘 내가 담임인 반에 있는 학생만 센다 — 강사 범위에는 다른 강사 반에 다니는 v1 담당 학생도
 * 들어 있어, 그대로 세면 반 명단 합보다 커진다(백엔드 8단계 전달 §1 · §4-F). 학생 표 · 「살펴볼 학생」은 범위 그대로다.
 * 「이번 주」는 오늘부터 7일 — 수업 요일 수를 센다.
 */
export function teacherKpis({ students = [], classes = [], schedules = [], today }) {
  const mine = new Set(classes.map((c) => c.class_id));
  const active = students.filter((s) => (s.state === 'enrolled' || s.state === 'paused') && s.classes.some((c) => mine.has(c.class_id)));
  const limit = addDays(today, 29);
  const soon = active
    .filter((s) => withdrawalDay(s) && withdrawalDay(s) >= today && withdrawalDay(s) <= limit)
    .sort((a, b) => withdrawalDay(a).localeCompare(withdrawalDay(b)));
  const perClass = classes.map((c) => ({ class_id: c.class_id, name: c.name, count: upcomingClassDays(c, [], today, 7).length }));
  const absences = [];
  classes.forEach((c) => {
    upcomingClassDays(c, schedules, today, 31).filter((d) => d.absent).forEach((d) => absences.push({ date: d.date, className: c.name, kind: d.absent }));
  });
  absences.sort((a, b) => a.date.localeCompare(b.date));
  return {
    myStudents: active.length,
    paused: active.filter((s) => s.state === 'paused').length,
    withdrawSoon: soon.length,
    withdrawSoonFirst: soon[0] ? { date: withdrawalDay(soon[0]), name: soon[0].student_name } : null,
    weekSessions: perClass.reduce((sum, c) => sum + c.count, 0),
    perClass,
    absences,
    weekEnd: addDays(today, 6),
  };
}

/** 반별 재원 — 강사에게는 반 인원(C1)이 빈 값이라 목록(S1)을 반별로 센다(요청서 §10) */
export const membersOf = (students, classId) => students.filter((s) => s.classes.some((c) => c.class_id === classId));

/** 살펴볼 학생 — 휴원 · 퇴원 예정 · 반 없음 · 학교 미지정 · 학교 갱신 필요 */
export function watchList(students = []) {
  return students
    .map((s) => {
      const reasons = [];
      if (s.withdraw_scheduled_on) reasons.push({ text: `퇴원 예정 ${formatMonthDay(withdrawalDay(s))}`, tone: 'danger' });
      if (s.state === 'paused') reasons.push({ text: '휴원', tone: 'warn' });
      if (s.classes.length === 0) reasons.push({ text: '반 없음', tone: 'muted' });
      // 13 졸업 · 14 비재학은 학교가 없는 것이 정상이다(백엔드 2단계 전달 §2)
      if (!s.school && !(s.grade >= 13)) reasons.push({ text: '학교 미지정', tone: 'muted' });
      else if (s.needs_school_update) reasons.push({ text: '학교 갱신 필요', tone: 'warn' });
      return { student: s, reasons };
    })
    .filter((w) => w.reasons.length > 0);
}

// ── 이력(설계 §4-1) ──

export const HISTORY_GROUPS = [
  { key: 'all', label: '전체' },
  { key: 'tenure', label: '재원' },
  { key: 'class', label: '반' },
  { key: 'school', label: '학교·부서' },
  { key: 'student', label: '학생 정보' },
  { key: 'note', label: '메모' },
  { key: 'correction', label: '정정' },
];

const TYPE_GROUP = [
  [/^(registered|re_registered|withdrawn|withdraw|pause|resume)/, 'tenure'],
  [/^(class_|transfer|enroll)/, 'class'],
  [/^(school|department|grade)/, 'school'],
  // 이름 · 로그인 이메일 · 비밀번호 · 연락처 · 학생번호를 고친 기록(student_updated — 백엔드 2단계 전달 §3 · §4-G)
  [/^student_/, 'student'],
  [/^note/, 'note'],
  [/^correct/, 'correction'],
];

/** N4 이력 한 줄의 묶음 — 종류 이름이 회신에서 달라져도 앞머리로 가른다. 정정 표시가 있으면 정정 */
export function historyGroupOf(item) {
  if (item.is_correction) return 'correction';
  const hit = TYPE_GROUP.find(([re]) => re.test(item.type || ''));
  return hit ? hit[1] : 'other';
}

/** 이력 점의 색 — 퇴원 빨강 · 이동 파랑 · 휴원·정정 노랑 · 메모 회색 · 나머지 초록 */
export function historyTone(item) {
  if (item.is_correction) return 'fix';
  if (/^withdraw/.test(item.type)) return 'danger';
  if (/^transfer/.test(item.type)) return 'info';
  if (/^pause|^resume/.test(item.type)) return 'warn';
  if (/^note/.test(item.type)) return 'muted';
  return 'ok';
}

// ── 숫자 ──

/** 서버 재원 수에는 휴원이 포함된다. 휴원 수가 없으면 추정하지 않는다. */
export function activeHeadcount(total, paused) {
  return [total, paused].every((value) => Number.isSafeInteger(value) && value >= 0) && paused <= total ? total - paused : null;
}

/** 퇴원율 = 휴원 포함 중단 횟수 ÷ (기간 말 수강 인원 + 중단 횟수). */
export function withdrawalStats(current, withdrawn) {
  const count = (value) => Number.isSafeInteger(value) && value >= 0 ? value : null;
  const enrolled = count(current);
  const left = count(withdrawn);
  const total = enrolled != null && left != null ? count(enrolled + left) : null;
  return { current: enrolled, withdrawn: left, total, rate: total > 0 ? left / total : null };
}

/** 0.0094 → '0.9%' */
export const formatRate = (r) => (r == null ? '—' : `${(r * 100).toFixed(1)}%`);
